/**
 * A storefront's social profiles, shown as icons in its footer. boxed parity
 * (`components/shared/footer/index.tsx` and `hooks/shared/useSocialLinks.ts` there): the same
 * six networks in the same order, except that boxed's links are constants and these are the
 * tenant's own, so a network the tenant has not set is not shown.
 *
 * Every value is an `https` URL and nothing else: tenant editors write these and the storefront
 * puts them in `href` attributes, and a plain `z.url()` accepts `javascript:`. The stored shape
 * is the wire shape (`storefront_configs.social_links`, `{}` = none), keyed by network, so a
 * network is set or absent and never `""`.
 */
import { z } from "zod";

/** The networks a storefront can link, in the order the footer shows them. */
export const SOCIAL_NETWORKS = [
  "facebook",
  "x",
  "discord",
  "instagram",
  "youtube",
  "tiktok",
] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

/** The one name per network: the dashboard's field label and the footer icon's accessible name. */
export const SOCIAL_NETWORK_LABELS: Record<SocialNetwork, string> = {
  facebook: "Facebook",
  x: "X",
  discord: "Discord",
  instagram: "Instagram",
  youtube: "YouTube",
  tiktok: "TikTok",
};

/** A profile URL as the footer links it: `https` only, so it can never run script. */
export const socialLinkUrlSchema = z.url({ protocol: /^https$/ }).max(2048);

/** The stored and wire shape: each set network's URL, no others. */
export const socialLinksSchema = z.partialRecord(z.enum(SOCIAL_NETWORKS), socialLinkUrlSchema);
export type SocialLinks = z.infer<typeof socialLinksSchema>;

/** The set networks, in footer order, each with its URL — what a footer or a summary iterates. */
export function listSocialLinks(links: SocialLinks): { network: SocialNetwork; url: string }[] {
  const out: { network: SocialNetwork; url: string }[] = [];
  for (const network of SOCIAL_NETWORKS) {
    const url = links[network];
    if (url) out.push({ network, url });
  }
  return out;
}
